import React, { useEffect, useCallback, useState, useRef } from "react";
import {
  ReactFlow,
  Background,
  useReactFlow,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "@xyflow/react";
import axios from "axios";
import { stratify, tree } from 'd3-hierarchy';

import { useCustomCampaignShowContext } from "./CustomCampaignShowContainer";
import WorkflowNode from "./Nodes/WorkflowNode";
import BuildingSidebar from "./Sidebars/BuildingSidebar";
import NodeSidebar from "./Sidebars/NodeSidebar";
import ControlPanel from "./ControlPanel";
import CustomCampaignInfoPanel from "./CustomCampaignInfoPanel";
import BeefreeComposer from "./EmailNode/BeefreeComposer";
import TwilioComposer from "./SmsNode/TwilioComposer";
import CustomCampaignHeader from "./CustomCampaignHeader";
import CustomCampaignTemplateHeader from "./CustomCampaignTemplateHeader";
import WorkflowEdge from "./WorkflowEdge";
import PathNode from "./Nodes/PathNode";
import CancelWorkflowExecutionsModal from "./Sidebars/CancelWorkflowExecutionsModal";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

let dummyPathNodeCount = 0;

const replaceArrayContents = (array, newContents) => {
  array.length = 0;
  array.push(...newContents);
};

const insertDummyPathNodes = (rootNode, edgeId, nodesToAdd, balancedNodes, balancedEdges) => {
  let currentEdge = balancedEdges.find(edge => edge.id === edgeId);
  let currentNode = balancedNodes.find(node => node.id === currentEdge.target);

  while (currentNode.node_data.root_branch_workflow_node_id !== rootNode.internal_id) {
    currentEdge = balancedEdges.find(edge => edge.source === currentNode.id);
    currentNode = balancedNodes.find(node => node.id === currentEdge.target);
  };

  const firstDummyPathNode = {
    id: `dummy-path-node-${dummyPathNodeCount}`,
    type: "WorkflowNodes::PathNode",
    node_data: {},
  }

  const firstDummyPathEdge = {
    id: `dummy-path-edge-${dummyPathNodeCount}`,
    isDummy: true,
    source: firstDummyPathNode.id,
    target: currentNode.id,
  }

  currentEdge.target = firstDummyPathNode.id;

  balancedEdges.push(firstDummyPathEdge);
  balancedNodes.push(firstDummyPathNode);

  dummyPathNodeCount++;

  for (let i = 1; i < nodesToAdd; i++) {
    let lastDummyEdge = balancedEdges[balancedEdges.length - 1];

    const newDummyNode = {
      id: `dummy-path-node-${dummyPathNodeCount}`,
      type: "WorkflowNodes::PathNode",
      node_data: {},
    }

    const newDummyEdge = {
      id: `dummy-path-edge-${dummyPathNodeCount}`,
      isDummy: true,
      source: newDummyNode.id,
      target: currentNode.id,
    }

    lastDummyEdge.target = newDummyNode.id;

    balancedEdges.push(newDummyEdge);
    balancedNodes.push(newDummyNode);

    dummyPathNodeCount++;
  }
};

const balanceBranch = (rootNode, processedNodes, processedEdges, balancedNodes, balancedEdges) => {
  const branchEdges = processedEdges.filter(edge => edge.source === rootNode.id);
  const pathData = {};

  branchEdges.forEach(edge => {
    pathData[edge.id] = analyzePath(rootNode, edge, processedNodes, processedEdges, balancedNodes, balancedEdges);
  });

  const maxPathLength = Math.max(...Object.values(pathData).map(data => data.pathLength));
  const terminalPathNode = pathData[branchEdges[0].id].terminalPathNode;

  Object.keys(pathData).forEach(edgeId => {
    const edgePathData = pathData[edgeId];
    const nodesToAdd = maxPathLength - edgePathData.pathLength;

    if (nodesToAdd) {
      insertDummyPathNodes(rootNode, edgeId, nodesToAdd, balancedNodes, balancedEdges);
    }
  });

  return {
    terminalPathNode: terminalPathNode,
    pathLength: maxPathLength,
  };
};

const analyzePath = (rootNode, rootEdge, processedNodes, processedEdges, balancedNodes, balancedEdges) => {
  let pathLength = 0;

  let currentNode = processedNodes.find(node => node.id === rootEdge.target);
  let currentEdge = processedEdges.find(edge => edge.source === currentNode.id);

  while (currentNode && currentEdge && currentNode.node_data.root_branch_workflow_node_id !== rootNode.internal_id) {
    pathLength++;

    if (currentNode.type === "WorkflowNodes::TrueFalseBranchNode") {
      const branchData = balanceBranch(currentNode, processedNodes, processedEdges, balancedNodes, balancedEdges);

      pathLength += branchData.pathLength;
      currentNode = branchData.terminalPathNode;
    } else {
      currentNode = processedNodes.find(node => node.id === currentEdge.target);
    }

    currentEdge = processedEdges.find(edge => edge.source === currentNode.id);
  }

  return {
    terminalPathNode: currentNode,
    pathLength: pathLength,
    currentEdge: currentEdge,
    currentNode: currentNode,
  };
};

const balanceWorkflow = (processedNodes, processedEdges) => {
  const triggerNode = processedNodes.find(node => node.type === "WorkflowNodes::TriggerNode");
  const triggerEdge = processedEdges.find(edge => edge.source === triggerNode.id);

  let balancedNodes = _.cloneDeep(processedNodes);
  let balancedEdges = _.cloneDeep(processedEdges);

  analyzePath(triggerNode, triggerEdge, processedNodes, processedEdges, balancedNodes, balancedEdges);

  replaceArrayContents(processedNodes, balancedNodes);
  replaceArrayContents(processedEdges, balancedEdges);
};

const positionNodes = (processedNodes, processedEdges) => {
  const layout = tree()
    .nodeSize([250, 100])
    .separation(() => 1);

  const hierarchy = stratify()
    .id((d) => d.id)
    .parentId((d) => processedEdges.find((e) => e.target === d.id)?.source)(
      processedNodes
    );

  replaceArrayContents(
    processedNodes,
    layout(hierarchy).descendants().map((d) => ({ ...d.data, position: { x: d.x, y: d.y } }))
  );
};

const alignChildren = (parentNode, processedNodes, processedEdges) => {
  let currentNode = parentNode;
  let currentEdge = processedEdges.find(edge => edge.source === currentNode.id);

  while (currentNode && currentEdge && currentNode.type !== "WorkflowNodes::TrueFalseBranchNode") {
    currentNode = processedNodes.find(node => node.id === currentEdge?.target);
    currentEdge = processedEdges.find(edge => edge.source === currentNode.id);

    currentNode.position.x = parentNode.position.x;
  };
};

const alignNodesOnXAxis = (processedNodes, processedEdges) => {
  processedNodes.filter(node => node.node_data?.root_branch_workflow_node_id).forEach(branchExitNode => {
    const parentNode = processedNodes.find(node => node.internal_id === branchExitNode.node_data.root_branch_workflow_node_id);

    branchExitNode.position.x = parentNode.position.x;

    alignChildren(branchExitNode, processedNodes, processedEdges);
  });
};

const CustomCampaignShow = () => {
  const {
    csrfToken,
    customCampaign,
    setCustomCampaign,
    buildPayload,
    building,
    showBeefreeComposer,
    showTwilioComposer,
    targetEdgeInternalId,
    setTargetEdgeInternalId,
    selectedNode,
    setSelectedNode,
    customCampaignBaseUrl,
    updateSelectedNodeData,
  } = useCustomCampaignShowContext();

  const { screenToFlowPosition, fitView } = useReactFlow();

  const [flowEdges, setFlowEdges, onFlowEdgesChange] = useEdgesState([]);
  const [flowNodes, setFlowNodes, onFlowNodesChange] = useNodesState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [changeDescription, setChangeDescription] = useState("");
  const callbackRef = useRef(null);

  const checkInProgressWorkflowExecutions = async () => {
    try {
      const params = new URLSearchParams();
      params.append('q[state_in][]', 'created');
      params.append('q[state_in][]', 'running');

      const response = await axios.get(`${customCampaignBaseUrl}/workflow_executions?${params.toString()}`);
      return response.data.total_count || 0;
    } catch (error) {
      toastError("Error checking workflow executions");
    }
  };

  const cancelInProgressWorkflowExecutions = async () => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      await axios.post(`${customCampaignBaseUrl}/cancel_in_progress_workflow_executions`);
      toastSuccess("Workflows cancelled");
    } catch (error) {
      toastError("Error cancelling workflows");
      throw error;
    }
  };

  const withWorkflowExecutionCheck = async (callback, description, skipCheck = false) => {
    if (skipCheck) {
      callback();
      return;
    }

    const count = await checkInProgressWorkflowExecutions();
    if (count > 0) {
      setInProgressCount(count);
      setChangeDescription(description);
      callbackRef.current = callback;
      setShowCancelModal(true);
      return;
    }

    callback();
  };

  const handleContinueWorkflows = () => {
    setShowCancelModal(false);
    if (callbackRef.current) {
      callbackRef.current();
      callbackRef.current = null;
    }
  };

  const handleCancelWorkflows = async () => {
    const pendingCallback = callbackRef.current;

    setShowCancelModal(false);
    callbackRef.current = null;

    try {
      await cancelInProgressWorkflowExecutions();

      if (pendingCallback) {
        pendingCallback();
      }
    } catch (error) {
      console.error("Error in handleCancelWorkflows:", error);
      toastError("Could not complete the action after cancellation");
    }
  };

  const handleCloseModal = () => {
    setShowCancelModal(false);
    callbackRef.current = null;
  };

  const checkForActiveExecutionsForPause = async () => {
    const count = await checkInProgressWorkflowExecutions();
    if (count > 0) {
      setInProgressCount(count);
      setChangeDescription("pausing this automation");
      callbackRef.current = pauseCampaignWithoutCancelling;
      setShowCancelModal(true);
    } else {
      pauseCampaignWithoutCancelling();
    }
  };

  const pauseCampaignWithoutCancelling = async () => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const { data } = await axios.patch(`${customCampaignBaseUrl}`, {
        custom_campaign: {
          state: "paused",
        },
      });

      toastSuccess("Automation paused");
      setCustomCampaign(data);
    } catch (error) {
      toastError("Error pausing automation");
    }
  };


  const updateCampaignState = async (newState) => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const { data } = await axios.patch(`${customCampaignBaseUrl}`, {
        custom_campaign: {
          state: newState,
        },
      });

      const message = newState === "active"
        ? "Automation resumed."
        : "Automation updated";
      toastSuccess(message);
      setCustomCampaign(data);
    } catch (error) {
      toastError("Error updating automation");
    }
  };

  const handleUpdateNode = (onSuccess) => {
    withWorkflowExecutionCheck(
      () => updateSelectedNodeData(onSuccess),
      "updating this node"
    );
  };

  const handleDeleteNode = (nodeInternalId) => {
    withWorkflowExecutionCheck(
      () => {
        axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
        axios.delete(`${customCampaignBaseUrl}/workflow_nodes/${nodeInternalId}`)
          .then(({ data }) => {
            setSelectedNode(null);
            setCustomCampaign(data);
            toastSuccess("Node deleted successfully");
          })
          .catch(error => {
            toastError("Error deleting node");
            console.error("error", error);
          });
      },
      "deleting this node"
    );
  };

  const debouncedFitViewOnNode = _.debounce((selectedNode) => {
    if (flowNodes.length > 5) {
      fitView(
        {
          nodes: [selectedNode],
          duration: 750,
          minZoom: 1.5,
          maxZoom: 1.5,
        }
      );
    } else {
      fitView(
        {
          nodes: flowNodes,
          duration: 750,
        }
      );
    }
  }, 50);

  useEffect(() => {
    if (!!selectedNode) {
      debouncedFitViewOnNode(selectedNode);
    }
  }, [selectedNode]);

  useEffect(() => {
    let processedNodes = _.cloneDeep(customCampaign.workflow_nodes);
    let processedEdges = _.cloneDeep(customCampaign.workflow_edges);

    balanceWorkflow(processedNodes, processedEdges);
    positionNodes(processedNodes, processedEdges);
    alignNodesOnXAxis(processedNodes, processedEdges);

    setFlowNodes(processedNodes.map((node) => {
      if (node.node_data?.root_branch_workflow_node_id) {
        node.position.x = processedNodes.find(n => n.internal_id === node.node_data.root_branch_workflow_node_id).position.x;
      }

      return {
        ...node,
        deletable: false,
        selectable: node.type !== "WorkflowNodes::ExitNode" && node.type !== "WorkflowNodes::PathNode",
        data: {
          ...node,
          handleClick: () => { setSelectedNode(node); },
        },
      }
    }));

    setFlowEdges(processedEdges.map((edge) => {
      const sourceNode = processedNodes.find(node => node.id === edge.source);
      const targetNode = processedNodes.find(node => node.id === edge.target);
      const buildable = !edge.isDummy && sourceNode.type !== "WorkflowNodes::TrueFalseBranchNode";
      const renderArrow = !targetNode.isDummy && targetNode.type !== "WorkflowNodes::PathNode";
      const midpointX = (sourceNode.position.x + targetNode.position.x) / 2;
      const midpointY = (sourceNode.position.y + targetNode.position.y) / 2;

      return {
        ...edge,
        selectable: false,
        deletable: false,
        markerEnd: {
          type: renderArrow ? MarkerType.ArrowClosed : MarkerType.None,
        },
        type: "workflow",
        data: {
          internalId: edge.internal_id,
          buildable,
          midpointX,
          midpointY,
        }
      }
    }));
  }, [customCampaign.workflow_nodes, customCampaign.workflow_edges]);

  const closestEdge = (node) => {
    let closestEdge = null;
    let minDistance = 75;

    flowEdges.forEach(edge => {
      if (edge.data.buildable) {
        const dx = edge.data.midpointX - node.position.x;
        const dy = edge.data.midpointY - node.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          minDistance = distance;
          closestEdge = edge;
        }
      }
    });

    return closestEdge;
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();

    if (!event.target.closest(".react-flow__pane")) {
      event.dataTransfer.dropEffect = "none";
      setTargetEdgeInternalId(0);
      return;
    }

    event.dataTransfer.dropEffect = "move";

    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    const newNode = {
      id: "onDropTempNode",
      position,
    };

    const targetEdge = closestEdge(newNode);

    setTargetEdgeInternalId(targetEdge?.internal_id || 0);
  }, [flowNodes, flowEdges]);

  const onDrop = useCallback((event) => {
    event.preventDefault();

    if (targetEdgeInternalId && event.target.closest(".react-flow__pane")) {
      withWorkflowExecutionCheck(
        () => {
          axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
          axios.post(`${customCampaignBaseUrl}/workflow_edges/${targetEdgeInternalId}/workflow_nodes`, {
            workflow_node: buildPayload,
          })
            .then(({ data: { custom_campaign, workflow_node } }) => {
              setSelectedNode(workflow_node);
              setCustomCampaign(custom_campaign);
              toastSuccess("Node added successfully");
            })
            .catch(error => {
              toastError("Error adding node");
              console.error("error", error);
            });
        },
        "adding this node"
      );
    }

    setTargetEdgeInternalId(0);
  }, [screenToFlowPosition, buildPayload, targetEdgeInternalId, customCampaign]);

  const nodeTypes = {
    "WorkflowNodes::TriggerNode": WorkflowNode,
    "WorkflowNodes::EmailNode": WorkflowNode,
    "WorkflowNodes::SmsNode": WorkflowNode,
    "WorkflowNodes::ManualSegmentUpdateNode": WorkflowNode,
    "WorkflowNodes::TimeDelayNode": WorkflowNode,
    "WorkflowNodes::WaitUntilNode": WorkflowNode,
    "WorkflowNodes::SendBetweenNode": WorkflowNode,
    "WorkflowNodes::TrueFalseBranchNode": WorkflowNode,
    "WorkflowNodes::PathNode": PathNode,
    "WorkflowNodes::ExitNode": WorkflowNode,
  };

  const edgeTypes = {
    workflow: WorkflowEdge,
  };

  const isTemplate = customCampaign.type === "CustomCampaigns::Template";

  return (
    <div style={{ height: isTemplate ? "95vh" : "80vh", display: "flex", flexDirection: "column" }}>
      <CancelWorkflowExecutionsModal
        open={showCancelModal}
        onClose={handleCloseModal}
        onContinue={handleContinueWorkflows}
        onCancel={handleCancelWorkflows}
        inProgressCount={inProgressCount}
        changeDescription={changeDescription}
      />
      {isTemplate ? <CustomCampaignTemplateHeader /> : (
        <CustomCampaignHeader
          checkForActiveExecutionsForPause={checkForActiveExecutionsForPause}
          updateCampaignState={updateCampaignState}
        />
      )}
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={onFlowNodesChange}
        onEdgesChange={onFlowEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodesDraggable={false}
        nodeOrigin={[0.5, 0.5]}
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={null}
        style={{ flex: 1, minHeight: 0, height: "auto" }}
        fitView
      >
        <ControlPanel handleDeleteNode={handleDeleteNode} />
        <CustomCampaignInfoPanel />
        { selectedNode ? <NodeSidebar handleUpdateNode={handleUpdateNode} /> : building ? <BuildingSidebar /> : null }
        <Background variant="dots" gap={24} size={2} />
      </ReactFlow>
      {showBeefreeComposer ? <BeefreeComposer /> : null}
      {showTwilioComposer ? <TwilioComposer /> : null}
    </div>
  );
};

export default CustomCampaignShow;
